import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { api, formatUSD, formatApiErrorDetail } from "@/lib/api";
import { ArrowUpRight, CloudArrowUp, Trash, Warning, Archive, ArrowCounterClockwise, Star, Plus, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { AdminCreateProjectModal } from "@/components/AdminCreateProjectModal";

const STATUSES = ["submitted", "preproduction", "design", "review", "final", "delivered"];

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("all");
  const [designerFilter, setDesignerFilter] = useState("all");
  const [needsDrive, setNeedsDrive] = useState(false);
  const [designers, setDesigners] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkConfirmText, setBulkConfirmText] = useState("");
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    try {
      const [p, d] = await Promise.all([
        api.get(`/projects?archived=false`),
        api.get("/admin/designers"),
      ]);
      setProjects(p.data);
      setDesigners(d.data);
    } catch (_) {}
  };

  useEffect(() => {
    load();
  }, []);

  const unarchive = async (id) => {
    try {
      await api.patch(`/projects/${id}/archive`, { archived: false });
      toast.success("Restored from archive");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const archive = async (id) => {
    if (!window.confirm("Move this project to the archive? It will be hidden from the default view.")) return;
    try {
      await api.patch(`/projects/${id}/archive`, { archived: true });
      toast.success("Archived");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const requestReview = async (p) => {
    if (!window.confirm(`Email ${p.client_email} a review request for "${p.title}"?`)) return;
    try {
      await api.post("/admin/reviews/request", { client_id: p.client_id, project_id: p.id });
      toast.success(`Review request emailed to ${p.client_email}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send");
    }
  };

  const markComplete = async (p) => {
    const msg = p.status === "delivered"
      ? `Archive "${p.title}"? It will move to the archive page.`
      : `Mark "${p.title}" delivered and move it to the archive?`;
    if (!window.confirm(msg)) return;
    try {
      if (p.status !== "delivered") {
        await api.patch(`/projects/${p.id}/status`, { status: "delivered" });
      }
      await api.patch(`/projects/${p.id}/archive`, { archived: true });
      toast.success("Marked complete — view in Archive");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not complete project");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleteConfirmText !== "DELETE") {
      toast.error("Type DELETE exactly to confirm");
      return;
    }
    setDeleting(true);
    try {
      const { data } = await api.delete(`/projects/${deleteTarget.id}`);
      const parts = [];
      if (data.deliverables_deleted) parts.push(`${data.deliverables_deleted} file${data.deliverables_deleted === 1 ? "" : "s"}`);
      if (data.messages_deleted) parts.push(`${data.messages_deleted} message${data.messages_deleted === 1 ? "" : "s"}`);
      const detail = parts.length ? ` (${parts.join(", ")})` : "";
      toast.success(`Deleted "${data.title}"${detail}`);
      setDeleteTarget(null);
      setDeleteConfirmText("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete project");
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = (visible) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = visible.every((p) => next.has(p.id));
      if (allSelected) {
        visible.forEach((p) => next.delete(p.id));
      } else {
        visible.forEach((p) => next.add(p.id));
      }
      return next;
    });
  };

  const confirmBulkDelete = async () => {
    if (bulkConfirmText !== "DELETE") {
      toast.error("Type DELETE exactly to confirm");
      return;
    }
    setBulkDeleting(true);
    try {
      const ids = Array.from(selected);
      const { data } = await api.post("/admin/projects/bulk-delete", { project_ids: ids });
      toast.success(`Deleted ${data.deleted_count}${data.not_found_count ? ` (${data.not_found_count} not found)` : ""}`);
      setSelected(new Set());
      setBulkConfirm(false);
      setBulkConfirmText("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  const filtered = projects.filter((p) => {
    if (filter !== "all" && p.status !== filter) return false;
    if (designerFilter === "unassigned" && p.assigned_designer_id) return false;
    if (designerFilter !== "all" && designerFilter !== "unassigned" && p.assigned_designer_id !== designerFilter) return false;
    if (needsDrive && !(p.unmirrored_count > 0)) return false;
    return true;
  });

  const totalUnmirrored = projects.reduce((acc, p) => acc + (p.unmirrored_count || 0), 0);

  return (
    <AdminLayout>
      <div className="mb-8 flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Projects</div>
            <h1 className="font-display text-5xl mt-2">All projects</h1>
          </div>
          <button
            onClick={() => setCreateOpen(true)}
            data-testid="open-create-project-modal"
            className="btn-primary px-4 py-2.5 text-xs uppercase tracking-wider flex items-center gap-2 self-start"
          >
            <Plus size={12} weight="bold" /> Add existing project
          </button>
        </div>
        <div className="flex flex-col md:flex-row gap-3 md:items-end">
          <div className="flex flex-wrap gap-2">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] self-center mr-1">Status:</span>
            <button
              onClick={() => setFilter("all")}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${filter === "all" ? "border-[#FF2D2D] text-[#FF2D2D]" : "border-[#262626] text-[#a3a3a3]"}`}
              data-testid="filter-all"
            >
              All
            </button>
            {STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${filter === s ? "border-[#FF2D2D] text-[#FF2D2D]" : "border-[#262626] text-[#a3a3a3]"}`}
                data-testid={`filter-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3]">Designer:</span>
            <select
              value={designerFilter}
              onChange={(e) => setDesignerFilter(e.target.value)}
              data-testid="filter-designer"
              className="bg-[#050505] border border-[#262626] px-3 py-1.5 text-xs focus:border-[#FF2D2D] outline-none"
            >
              <option value="all">All designers</option>
              <option value="unassigned">Unassigned</option>
              {designers.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => setNeedsDrive((v) => !v)}
            data-testid="filter-needs-drive"
            className={`flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${
              needsDrive
                ? "border-[#FF2D2D] bg-[#FF2D2D]/10 text-[#FF2D2D]"
                : "border-[#262626] text-[#a3a3a3] hover:border-[#FF2D2D] hover:text-[#FF2D2D]"
            }`}
            title="Show only projects with deliverables not yet mirrored to Google Drive"
          >
            <CloudArrowUp size={14} weight={needsDrive ? "fill" : "regular"} />
            Needs Drive {totalUnmirrored > 0 && (
              <span className="ml-1 text-[10px] tabular-nums px-1 py-0.5 bg-[#FF2D2D]/20 text-[#FF2D2D]">
                {totalUnmirrored}
              </span>
            )}
          </button>
          <Link
            to="/admin/archive"
            data-testid="goto-archive"
            className="flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-wider border border-[#262626] text-[#a3a3a3] hover:border-[#FF2D2D] hover:text-[#FF2D2D] transition-colors"
            title="View completed/archived projects"
          >
            <Archive size={14} /> View archive
          </Link>
        </div>
      </div>

      <div className="border border-[#262626] divide-y divide-[#262626]" data-testid="admin-projects-list">
        {/* Bulk action bar — shows when something is selected */}
        {selected.size > 0 && (
          <div className="bg-[#FF2D2D]/10 border-b border-[#FF2D2D] px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="bulk-bar">
            <div className="text-sm">
              <strong className="text-[#FF2D2D]">{selected.size}</strong> project{selected.size === 1 ? "" : "s"} selected
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSelected(new Set())}
                data-testid="bulk-clear"
                className="text-xs uppercase tracking-wider px-3 py-1.5 border border-[#262626] text-[#a3a3a3] hover:border-white hover:text-white"
              >
                Clear
              </button>
              <button
                onClick={() => { setBulkConfirmText(""); setBulkConfirm(true); }}
                data-testid="bulk-delete-btn"
                className="btn-primary text-xs px-3 py-1.5 uppercase tracking-wider flex items-center gap-1.5"
              >
                <Trash size={11} weight="bold" /> Delete {selected.size} selected
              </button>
            </div>
          </div>
        )}
        <div className="hidden md:grid grid-cols-12 gap-4 p-4 bg-[#0f0f0f] text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] items-center">
          <div className="col-span-1">
            <input
              type="checkbox"
              checked={filtered.length > 0 && filtered.every((p) => selected.has(p.id))}
              onChange={() => toggleSelectAllVisible(filtered)}
              data-testid="bulk-select-all"
              aria-label="Select all visible"
            />
          </div>
          <div className="col-span-3">Project</div>
          <div className="col-span-2">Client</div>
          <div className="col-span-2">Designer</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1">Total</div>
          <div className="col-span-1 text-right">Action</div>
        </div>
        {filtered.map((p) => (
          <div
            key={p.id}
            data-testid={`admin-project-${p.id}`}
            className={`grid grid-cols-12 gap-4 p-4 transition-colors items-center group ${
              selected.has(p.id) ? "bg-[#FF2D2D]/5 border-l-2 border-l-[#FF2D2D]" : "bg-[#0a0a0a] hover:bg-[#121212]"
            }`}
          >
            <div className="col-span-1 flex justify-start">
              <input
                type="checkbox"
                checked={selected.has(p.id)}
                onChange={() => toggleSelected(p.id)}
                data-testid={`bulk-select-${p.id}`}
                onClick={(e) => e.stopPropagation()}
                aria-label={`Select ${p.title}`}
              />
            </div>
            <Link to={`/admin/projects/${p.id}`} className="col-span-12 md:col-span-3 min-w-0">
              <div className="font-semibold truncate group-hover:text-[#FF2D2D] transition-colors flex items-center gap-2">
                {p.title}
                {p.archived && (
                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#404040] text-[#a3a3a3] shrink-0" data-testid={`archived-badge-${p.id}`}>
                    Archived
                  </span>
                )}
              </div>
              <div className="text-xs text-[#a3a3a3]">{p.service_type}</div>
            </Link>
            <Link to={`/admin/projects/${p.id}`} className="col-span-6 md:col-span-2 text-sm truncate">{p.client_name}</Link>
            <Link to={`/admin/projects/${p.id}`} className="col-span-6 md:col-span-2 text-sm">
              {p.assigned_designer_name ? (
                <span className="text-[#FF2D2D] font-semibold">{p.assigned_designer_name}</span>
              ) : (
                <span className="text-[#a3a3a3]">—</span>
              )}
            </Link>
            <Link to={`/admin/projects/${p.id}`} className="col-span-6 md:col-span-2 text-sm text-[#FF2D2D] capitalize">{p.status}</Link>
            <Link to={`/admin/projects/${p.id}`} className="col-span-6 md:col-span-1 text-sm">
              {p.payment_method === "points" ? `${p.total_points} pts` : formatUSD(p.total_usd)}
              {p.unmirrored_count > 0 && (
                <span
                  data-testid={`unmirrored-${p.id}`}
                  className="ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#FF2D2D]/40 text-[#FF2D2D] inline-flex items-center gap-1 align-middle"
                  title={`${p.unmirrored_count} deliverable${p.unmirrored_count === 1 ? "" : "s"} not yet mirrored to Drive`}
                >
                  <CloudArrowUp size={9} weight="bold" /> {p.unmirrored_count}
                </span>
              )}
            </Link>
            <div className="col-span-6 md:col-span-1 flex justify-end items-center gap-1">
              {p.archived ? (
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); unarchive(p.id); }}
                  data-testid={`unarchive-${p.id}`}
                  title="Restore from archive"
                  className="p-1.5 text-[#a3a3a3] hover:text-[#FF2D2D] opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <ArrowCounterClockwise size={14} />
                </button>
              ) : (
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); archive(p.id); }}
                  data-testid={`archive-${p.id}`}
                  title="Archive project"
                  className="p-1.5 text-[#a3a3a3] hover:text-[#FF2D2D] opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Archive size={14} />
                </button>
              )}
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteTarget(p); setDeleteConfirmText(""); }}
                data-testid={`delete-project-${p.id}`}
                title="Delete project"
                className="p-1.5 text-[#a3a3a3] hover:text-[#FF2D2D] opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash size={14} />
              </button>
              {p.status === "delivered" && !p.is_internal && (
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); requestReview(p); }}
                  data-testid={`request-review-row-${p.id}`}
                  title={p.last_review_request_at ? `Last requested ${new Date(p.last_review_request_at).toLocaleDateString()} — click to resend` : "Email this client a review request"}
                  className={`p-1.5 transition-opacity ${p.last_review_request_at ? "text-[#FF2D2D] opacity-100" : "text-[#a3a3a3] hover:text-[#FF2D2D] opacity-0 group-hover:opacity-100"}`}
                >
                  <Star size={14} weight={p.last_review_request_at ? "fill" : "regular"} />
                </button>
              )}
              <Link to={`/admin/projects/${p.id}`} className="p-1.5">
                <ArrowUpRight size={16} className="text-[#a3a3a3]" />
              </Link>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="bg-[#0a0a0a] p-8 text-center text-[#a3a3a3]">No projects in this view.</div>}
      </div>

      {/* Delete confirm modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="delete-project-modal">
          <div className="border border-[#FF2D2D] bg-[#0a0a0a] w-full max-w-md p-6">
            <div className="flex items-center gap-2 mb-3">
              <Warning size={20} weight="fill" className="text-[#FF2D2D]" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Permanent action</div>
            </div>
            <h3 className="font-display text-2xl">Delete this project?</h3>
            <div className="text-sm text-[#a3a3a3] mt-3 leading-relaxed">
              You're about to permanently delete <strong className="text-white">"{deleteTarget.title}"</strong> and all its data (deliverables, messages, surveys, sub-projects). There is no undo.
            </div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-5 mb-2">Type <code className="text-[#FF2D2D]">DELETE</code> to confirm</label>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              data-testid="delete-confirm-input"
              autoFocus
              placeholder="DELETE"
              className="w-full bg-[#050505] border border-[#262626] px-4 py-2.5 text-sm focus:border-[#FF2D2D] outline-none font-mono uppercase"
            />
            <div className="flex gap-3 mt-5">
              <button
                onClick={confirmDelete}
                disabled={deleting || deleteConfirmText !== "DELETE"}
                data-testid="delete-confirm-btn"
                className="btn-primary flex-1 px-4 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-40"
              >
                {deleting ? "Deleting…" : "Delete forever →"}
              </button>
              <button
                onClick={() => { setDeleteTarget(null); setDeleteConfirmText(""); }}
                disabled={deleting}
                data-testid="delete-cancel-btn"
                className="btn-outline flex-1 px-4 py-3 text-sm uppercase tracking-wider"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk delete modal */}
      {bulkConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="bulk-delete-modal">
          <div className="border border-[#FF2D2D] bg-[#0a0a0a] w-full max-w-md p-6">
            <div className="flex items-center gap-2 mb-3">
              <Warning size={20} weight="fill" className="text-[#FF2D2D]" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Permanent action</div>
            </div>
            <h3 className="font-display text-2xl">Delete {selected.size} projects?</h3>
            <div className="text-sm text-[#a3a3a3] mt-3 leading-relaxed">
              You're about to permanently delete <strong className="text-white">{selected.size} projects</strong> and all of their data — deliverables, messages, surveys, and sub-projects. There is no undo.
            </div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-5 mb-2">Type <code className="text-[#FF2D2D]">DELETE</code> to confirm</label>
            <input
              type="text"
              value={bulkConfirmText}
              onChange={(e) => setBulkConfirmText(e.target.value)}
              data-testid="bulk-confirm-input"
              autoFocus
              placeholder="DELETE"
              className="w-full bg-[#050505] border border-[#262626] px-4 py-2.5 text-sm focus:border-[#FF2D2D] outline-none font-mono uppercase"
            />
            <div className="flex gap-3 mt-5">
              <button
                onClick={confirmBulkDelete}
                disabled={bulkDeleting || bulkConfirmText !== "DELETE"}
                data-testid="bulk-confirm-btn"
                className="btn-primary flex-1 px-4 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-40"
              >
                {bulkDeleting ? "Deleting…" : `Delete ${selected.size} forever →`}
              </button>
              <button
                onClick={() => { setBulkConfirm(false); setBulkConfirmText(""); }}
                disabled={bulkDeleting}
                data-testid="bulk-cancel-btn"
                className="btn-outline flex-1 px-4 py-3 text-sm uppercase tracking-wider"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <AdminCreateProjectModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
      />
    </AdminLayout>
  );
}
